import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import spacetime from 'spacetime';
import { v4 as uuidv4 } from 'uuid';
import { useFormState } from 'react-final-form';

import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { tzMoscow } from 'CorePackage/MainModule/constants/timeZone';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { KNM_ACT } from 'CorePackage/MainModule/routes/routes';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ACTS_BLOCK_ID, getActBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmOrganizationTemplateSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataErpId,
  getKnmDataResigningRequired,
  getKnmErknmParticipantPositionsSelectOptions,
  getKnmFormRef,
  getKnmIsErknmStatusDeleted,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsTypeErknmPlanned,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { IParticipantPosition } from 'APIPackage/DictionariesModule/interfaces/IParticipantPosition';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { StructureRequirementsNpaModal } from 'KnmPackage/StructureRequirementsNpaModule/StructureRequirementsNpaModal';
import { IKnmErknmFormExpert } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormExpert';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { knmAddVisibleModal, knmSetIsNeedActSaveOrSign } from 'KnmPackage/MainModule/actions/knmActions';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { HadToBeHowInSignMessage } from 'KnmErknmPackage/MainModule/components/HadToBeHowInSignMessage';
import {
  ADD_ACT_ERROR_BY_EMPTY_SUBJECT_NOTIFICATION,
  ADD_ACT_ERROR_BY_NOT_FILLED_SUSPENSION,
  WAS_REMOVE_DUPLICATE_NPA,
} from 'CorePackage/MainModule/constants/notices';
import { getHasModifiedDirtyFields } from 'KnmPackage/MainModule/common/getHasModifiedDirtyFields';
import { getPlanListPlanErknmSigned } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { IRequirementsNpaItem } from 'KnmPackage/RequirementsNpaModalModule/interfaces/IRequirementsNpaItem';
import { InputFormRequirementsNpa } from 'KnmPackage/StructureRequirementsNpaModule/interface';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';

import styles from './ErknmActs.module.scss';
import { ErknmActsItem } from './ErknmActsItem';
import { IKnmErknmFormRequirementNpa } from '../../MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IRequirementsNpaInitValues } from '../../../KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { IKnmErknmFormViolatedRequirementsNpa } from '../../MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmErknmFormInspector } from '../../MainModule/interfaces/IKnmErknmFormInspector';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { getIsDirtyAct } from '../../../KnmPackage/KnmPaperlessActModule/KnmActSignButton/getIsDirtyAct';
import { IKnmErknmFormObject } from '../../MainModule/interfaces/IKnmErknmFormObject';
import { IKnmFormRiskIndicators } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRiskIndicators';
import { IKnmErknmFormEvent } from '../../MainModule/interfaces/IKnmErknmFormEvent';

interface IErknmActsProps {
  acts: IKnmErknmFormAct[] | undefined;
  events: IKnmErknmFormEvent[] | undefined;
  inspectorsErknm: IKnmErknmFormInspector[] | undefined;
  isDisabledAddingActByKnmImpossibleReason: boolean;
  kindControl?: IDictionarySupervisionTypeSelectOption;
  kindKnmCode: KnmKindCodesEnum | undefined;
  knoOrganization?: IDictionaryKnoOrganizationSelectOption;
  organizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] | undefined;
  requirementsNpa: IKnmErknmFormRequirementNpa[] | undefined;
  startDate: string | undefined;
  watchOnlyExtended: boolean;
  // isKnmKindRO: boolean;
  expertsErknm: IKnmErknmFormExpert[] | undefined;
  completedRO: boolean | undefined;
  organizations?: IKnmErknmFormSubject[] | undefined;
  objects?: IKnmErknmFormObject[];
  riskIndicators?: IKnmFormRiskIndicators[];
}

interface IErknmActsStoreProps {
  erpId: string | undefined;
  formRef: IKnmFormRef | undefined;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isKnmStatusTypeDeleted: boolean;
  isKnmStatusTypeWaitingCarriedOut: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isKnmTypeErknmPlanned: boolean;
  isSupervisor: boolean;
  participantPositionsSelectOptions: IDictionarySelectOption<IParticipantPosition>[] | undefined;
  planListPlanErknmSigned: string | undefined;
  resigningRequired: boolean;
  settings: IAdminSetting[] | undefined;
}

const ErknmActsWithoutStore = ({
  acts,
  completedRO,
  events,
  expertsErknm,
  inspectorsErknm,
  isDisabledAddingActByKnmImpossibleReason,
  kindControl,
  kindKnmCode,
  knoOrganization,
  objects,
  riskIndicators,
  organizations,
  organizationsTemplatesSelectOptions,
  requirementsNpa,
  startDate,
  watchOnlyExtended,
  erpId,
  formRef,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isKnmStatusTypeDeleted,
  isKnmStatusTypeWaitingCarriedOut,
  isKnmStatusTypeWaitingCarryOut,
  isKnmTypeErknmPlanned,
  isSupervisor,
  participantPositionsSelectOptions,
  planListPlanErknmSigned,
  resigningRequired,
  settings,
}: IErknmActsProps & IErknmActsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const userDate = spacetime.now(tzMoscow);
  const remove = formRef?.current.mutators.remove;

  const requirementsNpaFieldName = 'requirementsNpaErknm' as keyof IKnmForm;

  const isEnablePaperless = settings?.find(
    setting => setting.code === AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_ACT_PRESCRIPTION,
  );

  const isPaperless =
    kindKnmCode !== KnmKindCodesEnum.RO || !acts?.length
      ? isEnablePaperless?.value === 'true'
      : !!acts.find(i => i.paperless);

  // Если слишком много Актов - форма с открытыми блоками грузится слишком долго, поэтому по-умолчанию блоки свернуты, если их много
  const isClosedByManyActs = !!(acts && acts.length > 10);

  const addAct = useCallback(() => {
    const subjectNotifications = formRef?.current.getFieldState('subjectNotifications')?.value;
    const suspensionEvents = formRef?.current.getFieldState('suspensionEvents')?.value;

    if (suspensionEvents?.find(se => se.isNew)) {
      dispatch(
        addNoticeAction({
          renderContent: ADD_ACT_ERROR_BY_NOT_FILLED_SUSPENSION,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.ACT_ERRORS,
        }),
      );

      return;
    }

    if (
      (isKnmStatusTypeCarriedOut || isKnmStatusTypeWaitingCarriedOut) &&
      (!subjectNotifications || !subjectNotifications.length) &&
      kindKnmCode !== KnmKindCodesEnum.IV &&
      kindKnmCode !== KnmKindCodesEnum.MZ &&
      kindKnmCode !== KnmKindCodesEnum.KZ &&
      kindKnmCode !== KnmKindCodesEnum.VK
    ) {
      dispatch(
        addNoticeAction({
          renderContent: ADD_ACT_ERROR_BY_EMPTY_SUBJECT_NOTIFICATION,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.ACT_ERRORS,
        }),
      );

      return;
    }

    if (!watchOnlyExtended && push) {
      const addingAct: IKnmErknmFormAct = {
        extractVersion: undefined,
        involvedWitnesses: undefined,
        involvedExperts: undefined,
        acquaintanceLackNoInfoComment: undefined,
        acquaintanceLackType: undefined,
        acquaintanceType: undefined,
        canceledReason: undefined,
        canceledReasonDescription: undefined,
        created: undefined,
        createdBy: undefined,
        creationActDateTime: undefined,
        document: undefined,
        durationDays: undefined,
        durationHours: undefined,
        events: undefined,
        fioAndPositionSinger: undefined,
        fioReader: undefined,
        fioSigner: undefined,
        guid: undefined,
        holdingDateTime: undefined,
        isNew: true,
        isViolatedMandatoryRequirements: undefined,
        isViolationResolved: undefined,
        isViolationResolvedNote: undefined,
        knoInspectors: undefined,
        listId: uuidv4(),
        nextWordDayActDateTime: undefined,
        noteInvalidate: undefined,
        numberAct: isPaperless ? `${erpId}-${(acts?.length || 0) + 1}` : undefined,
        organization: undefined,
        organizationTemplate: undefined,
        refuserFio: undefined,
        refuserTitle: undefined,
        canceled: false,
        titleReader: undefined,
        titleSigner: undefined,
        typeAct: undefined,
        updated: undefined,
        updatedBy: undefined,
        violatedMandatoryRequirements: undefined,
        familiarizationNotifications: undefined,
        remarks: undefined,
        signed: undefined,
        signActDateTime: undefined,
        paperless: isPaperless,
        firstExtractDateTime: null,
        systemSignatureUsed: false,
        systemSignatureDateTime: null,
        systemSignatureMnemonic: null,
        systemSignatureTitle: null,
        signers: undefined,
      };

      push('acts', addingAct);
    }
  }, [
    push,
    watchOnlyExtended,
    isKnmStatusTypeWaitingCarriedOut,
    isKnmStatusTypeCarriedOut,
    KnmKindCodesEnum,
    isPaperless,
    erpId,
    acts,
    formRef,
  ]);

  const isDisableAddFieldByTime = userDate.isBefore(
    spacetime(formatDate({ date: startDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS }) || ''),
  );

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_ACT_TYPES));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_CANCELED_REASONS));
  }, [dispatch]);

  // @ts-ignore
  const isExistActs = formRef?.current.getFieldState('acts')?.value?.length > 0;
  // @ts-ignore
  const isExistNewActs = !!formRef?.current.getFieldState('acts')?.value?.find(i => i.isNew);

  const makeRemoveActHandler = (index: number) => () => {
    const actsData = formRef?.current.getFieldState('acts')?.value;

    if (actsData && !watchOnlyExtended && (!isKnmStatusTypeCompleted || actsData[index].isNew) && remove) {
      remove('acts', index);

      if (isPaperless) {
        const newActs = actsData
          .filter((_item, i) => i !== index)
          .map((act, aI) => ({
            ...act,
            numberAct: `${erpId}-${aI + 1}`,
          }));

        formRef?.current?.change('acts', newActs);
      }
    }
  };

  const [violatedRequirementsNpaFieldName, setViolatedRequirementsNpaFieldName] = useState('');
  const [areWithMrIds, setAreWithMrIds] = useState<boolean>(false);
  const [actFieldName, setActFieldName] = useState('');

  const openViolatedRequirementsNpaModal = (actName: string, fieldName: string) => {
    setViolatedRequirementsNpaFieldName(fieldName);
    setActFieldName(actName);

    setRequirementsNpaInitValuesSelected(undefined);

    setAreWithMrIds(true);
    setIsRequirementsModalVisible(true);
  };

  const addViolatedRequirementsNpa = (items: IRequirementsNpaItem[]) => {
    if (isPaperless) {
      // @ts-ignore
      const requirements = formRef?.current?.getFieldState(violatedRequirementsNpaFieldName)
        ?.value as IKnmErknmFormViolatedRequirementsNpa[];
      let hasFoundDuplicate = false;

      if (push) {
        items.forEach(item => {
          const newItem: IKnmErknmFormViolatedRequirementsNpa = {
            guid: item.guid,
            npa: item.npa,
            structuralUnit: item.structuralUnit,
            violatedMandatory:
              item?.mandatory?.map(e => ({
                mandatory: e,
                category: undefined,
                violations: null,
              })) || [],
            isNew: true,
          };

          if (requirements?.length) {
            const npaIndex = requirements.findIndex(
              req => req.npa.id === item.npa.id && req.structuralUnit.id === item.structuralUnit.id,
            );

            if (npaIndex > -1) {
              hasFoundDuplicate = true;
            } else {
              push(violatedRequirementsNpaFieldName, newItem);
            }
          } else {
            push(violatedRequirementsNpaFieldName, newItem);
          }
        });
      }

      if (hasFoundDuplicate) {
        dispatch(
          addNoticeAction({
            renderContent: WAS_REMOVE_DUPLICATE_NPA,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.NPA_CHECK,
          }),
        );
      }
    } else {
      push &&
        items.forEach(item => {
          const newItem: IKnmErknmFormViolatedRequirementsNpa = {
            guid: item.guid,
            npa: item.npa,
            structuralUnit: item.structuralUnit,
            violatedMandatory:
              item?.mandatory?.map(e => ({
                mandatory: e,
                category: undefined,
                violations: null,
              })) || [],
            isNew: true,
          };

          push(violatedRequirementsNpaFieldName, newItem);
          formRef?.current.change(
            `${actFieldName}.isViolatedMandatoryRequirements` as keyof IKnmForm,
            booleanSelectOptions.find(x => x.value === true),
          );
        });
    }
  };

  const [isRequirementsModalVisible, setIsRequirementsModalVisible] = useState(false);
  const [
    requirementsNpaInitValuesSelected,
    setRequirementsNpaInitValuesSelected,
  ] = useState<IRequirementsNpaInitValues>();

  const openRequirementsModal = (
    initialValue: IRequirementsNpaInitValues,
    fieldName: string,
    parentFieldName?: string,
  ) => {
    if (isPaperless && parentFieldName) {
      setViolatedRequirementsNpaFieldName(parentFieldName);
    }
    setRequirementsNpaInitValuesSelected(initialValue);
    setAreWithMrIds(false);
    setIsRequirementsModalVisible(true);
  };

  const handleOpenFinishPassportModal = () => dispatch(knmAddVisibleModal(KnmModalsEnum.ERKNM_FINISH_RO_PASSPORT));

  const filledInspectors = useMemo(() => {
    if (!inspectorsErknm || inspectorsErknm.length === 0) return [];

    return inspectorsErknm.filter(i => !!i.fullName && !!i.position?.label);
  }, [inspectorsErknm]);

  const isDisabledAddActsByInspectorsErknm = useMemo(() => {
    return filledInspectors.length === 0;
  }, [filledInspectors]);

  /**
   * Расчет блокировки кнопки "Добавить" при наличии изменений в паспорте (за исключением актов)
   */
  const { dirtyFields: currentDirtyFields, dirty: isDirty, values } = useFormState({
    subscription: { dirtyFields: true, dirty: true, values: true },
  });

  const isDisabledByDirty = useMemo(() => {
    if ((isKnmStatusTypeCarriedOut || isKnmStatusTypeWaitingCarryOut || isKnmStatusTypeCompleted) && isPaperless) {
      const currentActs = formRef?.current.getFieldState('acts')?.value;

      // Исключение полей акта для проверки на modified
      const formDirtyFieldsWithoutActs = Object.entries(currentDirtyFields).reduce((acc, value) => {
        return !value[0].includes('acts') ? { ...acc, [value[0]]: value[1] } : acc;
      }, {});

      const hasDirtyAct =
        !!currentActs &&
        currentActs.some((item, index) => getIsDirtyAct(currentDirtyFields, index, false, false, formRef).wasEditFiled);

      const hasModifiedDirtyFields = getHasModifiedDirtyFields(formDirtyFieldsWithoutActs, formRef);

      return !hasDirtyAct && isDirty && hasModifiedDirtyFields;
    }

    return false;
  }, [
    formRef,
    isKnmStatusTypeCarriedOut,
    isKnmStatusTypeWaitingCarryOut,
    isKnmStatusTypeCompleted,
    currentDirtyFields,
    isDirty,
    values,
  ]);

  /**
   * Проверка наличия необходимости сохранения или подписания одного из актов
   */
  useEffect(() => {
    if ((isKnmStatusTypeCarriedOut || isKnmStatusTypeWaitingCarryOut || isKnmStatusTypeCompleted) && isPaperless) {
      if (acts && acts.length > 0) {
        const isNeedActSaveOrSign = acts.some((act, index) => {
          const isEnablePaperlessDocument = isLaterThanAdminSetting(
            act.created,
            settings,
            AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
          );

          // Если отключена настройка "безбумаги" или текущий акт был создан в старой версии, то проверка несохраненных полей отключается
          if (!isEnablePaperlessDocument || !act.paperless) {
            return false;
          }

          if (!act.isNew) {
            // Алгоритм аналогичный KnmActSignButton
            if (act.signed) {
              const { wasEditFiled: isDirtyBySigned } = getIsDirtyAct(currentDirtyFields, index, false, false, formRef);

              return isDirtyBySigned;
            }

            return true;
          }

          return true;
        });

        dispatch(knmSetIsNeedActSaveOrSign(isNeedActSaveOrSign));
      } else {
        dispatch(knmSetIsNeedActSaveOrSign(false));
      }
    }

    return () => {
      dispatch(knmSetIsNeedActSaveOrSign(false));
    };
  }, [
    acts,
    isDirty,
    isKnmStatusTypeCarriedOut,
    isKnmStatusTypeWaitingCarryOut,
    isKnmStatusTypeCompleted,
    currentDirtyFields,
  ]);

  const addActButton = () => {
    return (
      <Button
        className="shared-btn-add"
        text="Добавить"
        type="button"
        variant="link"
        color="primary"
        disabled={
          watchOnlyExtended ||
          (kindKnmCode !== KnmKindCodesEnum.RO && isExistActs) ||
          (kindKnmCode === KnmKindCodesEnum.RO && isExistNewActs) ||
          (isKnmStatusTypeDeleted && isSupervisor) ||
          resigningRequired ||
          isDisabledAddActsByInspectorsErknm ||
          completedRO ||
          isDisabledByDirty ||
          (isKnmTypeErknmPlanned ? !planListPlanErknmSigned : false)
        }
        onClick={addAct}
      />
    );
  };

  const isShowButtonFinishAct = useMemo(() => {
    return (
      isPaperless &&
      kindKnmCode === KnmKindCodesEnum.RO &&
      !isKnmStatusTypeCompleted &&
      !!acts?.find(i => i.signed) &&
      !organizations?.length
    );
  }, [isPaperless, kindKnmCode, isKnmStatusTypeCompleted, acts, organizations]);

  const isDisabledButtonFinishAct = useMemo(() => {
    return acts ? acts?.filter(i => !i.signed).length > 0 : true;
  }, [acts]);

  const helpHit = useMemo(() => {
    if (!isKnmStatusTypeCarriedOut && !isKnmStatusTypeWaitingCarryOut && !isKnmStatusTypeCompleted) {
      return undefined;
    }
    if (acts && acts.find(i => !i.paperless)) {
      return undefined;
    }
    if (isKnmTypeErknmPlanned && !planListPlanErknmSigned) {
      return <HadToBeHowInSignMessage message={'План не подписан, нельзя добавить акт'} />;
    }
    if (kindKnmCode === KnmKindCodesEnum.RO && acts && acts.length > 0 && !!acts.find(i => !i.isNew && !i.signed)) {
      return <HadToBeHowInSignMessage message={'Акт можно добавить только после подписания предыдущего акта'} />;
    }
    if (kindKnmCode !== KnmKindCodesEnum.RO && acts && acts.length > 0 && !!acts.find(i => !i.isNew && !i.signed)) {
      return <HadToBeHowInSignMessage message={'Необходимо переподписать изменения в акте'} />;
    }
    if (isDisabledByDirty || resigningRequired) {
      return <HadToBeHowInSignMessage message={'Необходимо сохранить и подписать изменения в решении'} />;
    }
    if (isDisabledAddActsByInspectorsErknm) {
      return <HadToBeHowInSignMessage message={'Необходимо заполнить должностные лица'} />;
    }

    return undefined;
  }, [
    kindKnmCode,
    acts,
    isDisabledByDirty,
    resigningRequired,
    isDisabledAddActsByInspectorsErknm,
    isKnmStatusTypeCarriedOut,
    isKnmStatusTypeWaitingCarryOut,
    isKnmStatusTypeCompleted,
    isKnmTypeErknmPlanned,
    planListPlanErknmSigned,
  ]);

  /**
   * Конвертированные НПА для модального окна выбора
   */
  const convertedInputRequirementsNpa = useMemo(() => {
    if (!requirementsNpa) return [];

    return requirementsNpa.map(npa => {
      return {
        ...npa,
        mandatory: npa.mandatory?.map(mand => mand.mandatory),
      } as InputFormRequirementsNpa;
    });
  }, [requirementsNpa]);

  const requirementsNpaFieldValue = formRef?.current.getFieldState(requirementsNpaFieldName)
    ?.value as IKnmErknmFormRequirementNpa[];

  // забираем список id mandatory из блока "Предмет контрольного (надзорного) мероприятия"
  // для использования в модальном окне добавления обязательного требования в акте (нарушения)
  const mrIds = useMemo(() => {
    if (requirementsNpaFieldValue) {
      const set = new Set();

      requirementsNpaFieldValue?.forEach(el => {
        el.mandatory?.forEach(item => {
          const val = item?.mandatory?.id;

          if (val) {
            set.add(val);
          }
        });
      });

      return Array.from(set);
    }

    return undefined;
  }, [requirementsNpaFieldValue]) as number[] | undefined;

  return (
    <>
      <Section
        title={
          <>
            Сведения об акте
            <ErknmFieldMark fieldName="acts" />
          </>
        }
        id={KNM_ACT}
        titleId={ACTS_BLOCK_ID}
        type="list"
        customButton={
          startDate && isDisableAddFieldByTime && isDisabledAddingActByKnmImpossibleReason ? (
            <Tooltip
              placement="top"
              content={
                <div className={styles.TooltipContent}>
                  Дата составления акта КНМ не может быть раньше даты начала проведения КНМ
                </div>
              }
            >
              <span className={styles.TooltipContainer}>{addActButton()}</span>
            </Tooltip>
          ) : (
            addActButton()
          )
        }
      >
        <div>{helpHit}</div>
        <FieldArray name="acts">
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormAct, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((name, index) => {
              const fieldValue: IKnmErknmFormAct = fields.value?.[index];
              const id = getActBlockId(fieldValue.listId);

              return (
                <ErknmActsItem
                  key={id}
                  isPaperless={!!fieldValue.paperless}
                  value={fieldValue}
                  id={id}
                  name={name}
                  isWatchOnly={watchOnlyExtended}
                  isDisabledByDirty={isDisabledByDirty}
                  disableActAddingByImpossibleStatus={isDisabledAddingActByKnmImpossibleReason}
                  kindKnmCode={kindKnmCode}
                  organizationsTemplatesSelectOptions={organizationsTemplatesSelectOptions}
                  onRemove={makeRemoveActHandler(index)}
                  index={index}
                  addRequirementsNpa={openViolatedRequirementsNpaModal}
                  addViolatedMandatory={openRequirementsModal}
                  participantPositionsSelectOptions={participantPositionsSelectOptions}
                  inspectorsErknm={filledInspectors}
                  expertsErknm={expertsErknm}
                  isPm={false}
                  isDecision={false}
                  acts={acts}
                  events={events}
                  isClosedByManyActs={isClosedByManyActs}
                />
              );
            });
          }}
        </FieldArray>
        {isShowButtonFinishAct && (
          <Button
            className={styles.SignButton}
            text="Завершить мероприятие"
            color="primary"
            id="finishKnmRo"
            type="button"
            disabled={isDisabledButtonFinishAct}
            onClick={handleOpenFinishPassportModal}
          />
        )}
      </Section>
      {isRequirementsModalVisible && (
        <StructureRequirementsNpaModal
          onClose={() => setIsRequirementsModalVisible(false)}
          knoVersionId={knoOrganization?.value.dictVersionId || ''}
          supervisionVersionId={kindControl?.value.dictVersionId || ''}
          withRequirements={true}
          addRequirementsNpa={addViolatedRequirementsNpa}
          initValues={requirementsNpaInitValuesSelected}
          inputRequirementsNpa={convertedInputRequirementsNpa}
          objects={objects}
          riskIndicators={riskIndicators}
          mrIds={areWithMrIds ? mrIds : undefined}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmActsStoreProps => ({
  formRef: getKnmFormRef(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isKnmStatusTypeDeleted: getKnmIsErknmStatusDeleted(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  participantPositionsSelectOptions: getKnmErknmParticipantPositionsSelectOptions(state),
  isKnmStatusTypeWaitingCarriedOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  resigningRequired: getKnmDataResigningRequired(state),
  settings: getAdminFetchSettings(state),
  erpId: getKnmDataErpId(state),
  planListPlanErknmSigned: getPlanListPlanErknmSigned(state),
  isKnmTypeErknmPlanned: getKnmIsTypeErknmPlanned(state),
});

export const ErknmActs = connect(mapStateToProps)(ErknmActsWithoutStore);
